import React from 'react';
import TranslationNamespaces from '../../locales/TranslationNamespaces';
import { useTranslation } from 'react-i18next';
import { QInputForm, QSpinner } from '@kubiq-base/core';
import { FormProvider, useForm } from 'react-hook-form';
import { useApi } from '../../state/ApiContext';
import { useCache } from '../../state/CachingContext';
import CenteredQButton from '../Shared/CenteredQButton';
import HeaderWithDescription from '../Shared/HeaderWithDescription';
import IdenticalPasswordForm from '../Shared/IdenticalPasswordForm';
import MessageBox from '../Shared/MessageBoxes/MessageBox';
import FlexFormContainer from '../Shared/FlexContainers/FlexFormContainer';
import FlexContainer from '../Shared/FlexContainers/FlexContainer';
import PageError from '../Shared/PageError';

type FormData = {
  password: string;
  passwordRep: string;
  smsCode: string;
};

const VerifyPasswordReset = (): JSX.Element => {
  const namespace = TranslationNamespaces.recovery;
  const { t } = useTranslation(namespace);
  const { postRequest } = useApi();
  const { getCache, clearCache } = useCache();
  const session = getCache();

  const { control, handleSubmit, formState, ...methods } = useForm<FormData>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  const [isVerifyingEmail, setIsVerifyingEmail] = React.useState(true);
  const [showErrorMessage, setShowErrorMessage] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [resetComplete, setResetComplete] = React.useState(false);
  const [codeError, setCodeError] = React.useState<string | undefined>();

  const onSubmit = async (data: { password: string; smsCode: string }): Promise<void> => {
    if (session && session.email && session.code && data) {
      setCodeError(undefined);

      await postRequest(
        'ResetPassword',
        { token: session.code, email: session.email, password: data.password, verificationCode: data.smsCode },
        setIsSubmitting,
        ['invalidCode'],
      )
        .then(() => {
          setResetComplete(true);
          clearCache('token');
        })
        .catch((err) => {
          if (err.response?.data.errors.global[0] === 'invalidCode') setCodeError('invalidCode');
          else setShowErrorMessage(true);
        });
    }
  };

  const bootstrapAsync = async (): Promise<void> => {
    if (session.email && session.code) {
      await postRequest('ResetPassword/VerifyEmailToken', {
        email: session.email,
        verificationCode: session.code,
      })
        .then(() => {
          setIsVerifyingEmail(false);
          postRequest('Sms/SendVerificationSms', {}); // Needs to be ratelimited in BE
        })
        .catch(() => setShowErrorMessage(true));
    } else {
      setShowErrorMessage(true);
    }
  };

  React.useEffect(() => {
    bootstrapAsync();
  }, []);

  return (
    <>
      {showErrorMessage ? (
        <PageError message='wentWrong' translationNamespace={TranslationNamespaces.global} />
      ) : isVerifyingEmail ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 72 }}>
          <QSpinner />
        </div>
      ) : !resetComplete ? (
        <>
          <HeaderWithDescription
            translationNamespace={namespace}
            headerSuffix='NewPassword'
            description='descriptionEnterPassword'
          >
            <MessageBox text={session.email as string} emailBox />
          </HeaderWithDescription>

          <FormProvider control={control} handleSubmit={handleSubmit} formState={formState} {...methods}>
            <FlexFormContainer onSubmit={handleSubmit(onSubmit)}>
              <IdenticalPasswordForm translationNamespace={namespace} />

              <QInputForm
                control={control}
                name='smsCode'
                label={t('smsCode')}
                rules={{ required: 'requiredField' }}
                isError={
                  (formState.errors.smsCode && t(formState.errors.smsCode.message as string)) ||
                  (codeError && t(codeError as string))
                }
              />

              <CenteredQButton type='submit' isLoading={isSubmitting} color='secondary' fullWidth>
                {t('next')}
              </CenteredQButton>
            </FlexFormContainer>
          </FormProvider>

          <FlexContainer>
            <CenteredQButton to='/' variant='text'>
              {t('cancel')}
            </CenteredQButton>
          </FlexContainer>
        </>
      ) : (
        <>
          <HeaderWithDescription
            translationNamespace={namespace}
            headerSuffix='Complete'
            description='descriptionPWComplete'
          />

          <FlexContainer>
            <CenteredQButton to='/' color='secondary' fullWidth>
              {t('login')}
            </CenteredQButton>
          </FlexContainer>
        </>
      )}
    </>
  );
};

export default VerifyPasswordReset;
